import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useServices } from '@/components/ServiceContext';
import { ListType } from '@/service/ExportJobService';
import cronParser from 'cron-parser';
import humanizeDuration from 'humanize-duration';
import cronstrue from 'cronstrue';
import log from 'loglevel';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import {
  ReportDefinition,
  reportDefinitions,
} from '@lambda-layer/constants/reportDefinitions';
import { UserContext } from '@/context/userContext.tsx';
import { isUserInGroup } from '@/auth/EricksonAuthenticator.tsx';
import { useMessageQueue } from '@erickson/components';

interface ReportRow extends ReportDefinition {
  id: string;
  schedule?: string; // human-readable schedule text
  runTime?: string; // human-readable next run time
  rawScheduleExpression?: string; // the raw expression for editing
  scheduleEnabled?: boolean;
  scheduleId?: string; // unique id of the schedule record (if exists)
  subscribers?: string[]; // list of user subs subscribed to this report
}

// Helper to convert a schedule expression into human-readable text.
const getScheduledText = (scheduleExpression?: string): string => {
  if (!scheduleExpression) return 'Not scheduled';
  const cronMatch = scheduleExpression.match(/cron\(([^)]+)\)/);
  if (cronMatch) {
    return cronstrue.toString(cronMatch[1], { use24HourTimeFormat: true });
  } else {
    return 'Unknown format';
  }
};

// Get the next run time text based on a cron expression.
const getNextRunText = (value?: string): string => {
  const cron = value?.match(/cron\(([^)]+)\)/)?.[1];
  return cron ? parseCronExpression(cron) : '';
};

const parseCronExpression = (cron: string): string => {
  try {
    const standardizedCron = cron
      .replace(/\?/g, '*')
      .split(' ')
      .slice(0, 5)
      .join(' ');
    const interval = cronParser.parse(standardizedCron);
    const nextDate = interval.next().toDate();
    const duration = humanizeDuration(nextDate.getTime() - Date.now(), {
      largest: 2,
      round: true,
    });
    return `Next run in ${duration}`;
  } catch (error) {
    log.error('Failed to parse cron expression:', cron, error);
    return 'Invalid schedule';
  }
};

interface ReportsPageProps {
  listType?: ListType;
}

const ReportsPage: React.FC<ReportsPageProps> = ({ listType }) => {
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const { routeService, reportScheduleService } = useServices();
  const messageQueue = useRef(useMessageQueue()).current;

  const [reportRows, setReportRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const isAdmin = isUserInGroup(userContext.user, 'Admin');

  const filteredReportDefinitions = useMemo(() => {
    return reportDefinitions.filter(report => {
      if (report.id === 'report-bl-acxiom-do-not-mail') {
        return isAdmin;
      }
      return !listType || report.listType === listType;
    });
  }, [isAdmin, listType]);

  // Fetch schedules and enrich report definitions.
  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const reportSchedules = await reportScheduleService.list();
      // Map reportId to schedule details (including subscribers)
      const reportSchedulesMap = new Map<
        string,
        {
          scheduleExpression?: string;
          enabled?: boolean;
          scheduleId?: string;
          subscribers?: string[];
        }
      >(
        reportSchedules.items.map(schedule => [
          schedule.reportId,
          {
            scheduleExpression: schedule.scheduleExpression,
            enabled: schedule.enabled,
            scheduleId: schedule.id,
            subscribers: schedule.subscribers,
          },
        ])
      );

      const enrichedReports: ReportRow[] = filteredReportDefinitions
        .map(report => {
          const schedule = reportSchedulesMap.get(report.id);
          return {
            ...report,
            schedule: schedule?.scheduleExpression
              ? getScheduledText(schedule.scheduleExpression)
              : 'Not scheduled',
            runTime: schedule?.scheduleExpression
              ? getNextRunText(schedule.scheduleExpression)
              : '',
            rawScheduleExpression: schedule?.scheduleExpression || '',
            scheduleEnabled: schedule ? schedule.enabled : false,
            scheduleId: schedule?.scheduleId,
            subscribers: schedule?.subscribers || [],
          };
        })
        .sort(
          (a, b) =>
            a.listType.localeCompare(b.listType) ||
            a.reportName.localeCompare(b.reportName)
        );

      setReportRows(enrichedReports);
    } catch (err) {
      log.error('Failed to fetch report schedules:', err);
      messageQueue.addMessage({
        text: 'Failed to load report schedules. Please try again later.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [reportScheduleService, filteredReportDefinitions, messageQueue]);

  useEffect(() => {
    void fetchSchedules();
  }, [fetchSchedules]);

  // State for editing schedule.
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportRow | null>(null);
  const [scheduleExpression, setScheduleExpression] = useState('');
  const [enabled, setEnabled] = useState(false);

  // Navigate to the report details page.
  const navigateToReport = useCallback(
    (reportId: string) => {
      const route = routeService.getRoute(reportId)?.path || '/';
      navigate(route, { state: { viewTransition: true } });
    },
    [navigate, routeService]
  );

  // Edit schedule button click handler.
  const handleEditClick = useCallback((report: ReportRow) => {
    setSelectedReport(report);
    setScheduleExpression(report.rawScheduleExpression || '');
    setEnabled(report.scheduleEnabled || false);
    setOpenEdit(true);
  }, []);

  // Save schedule changes.
  const handleSave = async () => {
    if (!selectedReport) return;
    try {
      const payload = {
        reportId: selectedReport.id,
        scheduleExpression,
        enabled,
      };

      // Save or update the schedule based on whether it already exists.
      if (selectedReport.scheduleId) {
        await reportScheduleService.update({
          ...payload,
          id: selectedReport.scheduleId,
        });
      } else {
        await reportScheduleService.save({ ...payload, subscribers: [] });
      }

      setOpenEdit(false);
      await fetchSchedules();
    } catch (err) {
      log.error('Failed to update schedule:', err);
      messageQueue.addMessage({
        text: 'Failed to update schedule. Please try again later.',
        severity: 'error',
      });
    }
  };

  // Toggle subscription (subscribe/unsubscribe) for the current user.
  const handleToggleSubscription = useCallback(
    async (report: ReportRow) => {
      if (!userContext.user?.userId) return;
      const isSubscribed = report.subscribers?.includes(
        userContext.user.userId
      );
      try {
        await reportScheduleService.updateSubscription({
          reportId: report.id,
          subscribe: !isSubscribed,
        });
        await fetchSchedules();
      } catch (error) {
        log.error('Subscription update failed:', error);
        messageQueue.addMessage({
          text: 'Failed to update subscription. Please try again later.',
          severity: 'error',
        });
      }
    },
    [userContext.user, reportScheduleService, fetchSchedules, messageQueue]
  );

  // Define columns for DataGrid, combining subscribe and admin edit actions.
  const reportColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'reportName',
        headerName: 'Report Name',
        width: 400,
      },
      {
        field: 'listType',
        headerName: 'List Type',
        width: 120,
        valueFormatter: listTypeString => {
          switch (listTypeString) {
            case 'boughtlist':
              return 'Bought List';
            case 'houselist':
              return 'House List';
            case 'staffseed':
              return 'Staff Seed';
          }
          return listTypeString;
        },
      },
      {
        field: 'reportDescription',
        headerName: 'Report Description',
        width: 320,
      },
      {
        field: 'schedule',
        headerName: 'Scheduled',
        width: 300,
      },
      {
        field: 'runTime',
        headerName: 'Next Run Time',
        width: 250,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        renderCell: params => {
          const actions = [];
          const currentUserId = userContext.user?.userId;
          if (isUserInGroup(userContext.user, 'Admin')) {
            const scheduleEnabled = params.row.scheduleEnabled;
            actions.push(
              <GridActionsCellItem
                key="edit"
                icon={
                  <Tooltip
                    title={
                      scheduleEnabled
                        ? 'Schedule enabled: click to modify schedule'
                        : 'Schedule disabled: click to enable or modify schedule'
                    }
                  >
                    {scheduleEnabled ? (
                      <EventAvailableIcon />
                    ) : (
                      <EventBusyIcon />
                    )}
                  </Tooltip>
                }
                label="Edit Schedule"
                onClick={e => {
                  e.stopPropagation();
                  handleEditClick(params.row);
                }}
                color={scheduleEnabled ? 'inherit' : 'error'}
              />
            );
          }
          const isSubscribed = params.row.subscribers?.includes(currentUserId);
          actions.push(
            <GridActionsCellItem
              key="subscription"
              icon={
                isSubscribed ? (
                  <Tooltip title="Click to unsubscribe from report notifications">
                    <NotificationsOffIcon />
                  </Tooltip>
                ) : (
                  <Tooltip title="Click to subscribe for report notifications">
                    <NotificationsNoneIcon />
                  </Tooltip>
                )
              }
              label={isSubscribed ? 'Unsubscribe' : 'Subscribe'}
              onClick={e => {
                e.stopPropagation();
                void handleToggleSubscription(params.row);
              }}
              color="inherit"
            />
          );
          return actions;
        },
      },
    ],
    [handleToggleSubscription, handleEditClick, userContext.user]
  );

  return (
    <Box ml={3} mr={3} mt={2}>
      <Typography variant="h2" mb={2} fontWeight={400}>
        Reports
      </Typography>
      <DataGrid
        columns={reportColumns}
        rows={reportRows}
        rowSelection={false}
        onRowClick={({ id }) => navigateToReport(id.toString())}
        loading={loading}
      />
      {/* Modal for editing schedule */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Schedule</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Specify the report schedule using a cron expression.
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            For example:
            <br />
            <strong>Cron:</strong> <code>cron(0 12 * * ? *)</code> for daily
            execution at noon.
          </Typography>
          <TextField
            fullWidth
            label="Schedule (cron expression)"
            value={scheduleExpression}
            onChange={e => setScheduleExpression(e.target.value)}
            margin="normal"
            helperText="Enter a valid cron (e.g. cron(0 12 * * ? *)) expression."
          />
          {scheduleExpression && (
            <Typography variant="body2" color="textSecondary">
              Preview: {getScheduledText(scheduleExpression)}
              {getNextRunText(scheduleExpression) &&
                ` - ${getNextRunText(scheduleExpression)}`}
            </Typography>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
              />
            }
            label="Enabled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsPage;
